import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { merge, of, Subject, takeUntil } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { HeaderStore, headerUserView } from './header.store';

interface HeaderUserView {
  displayName: string;
  email: string;
  phone: string;
  initials: string;
}

@Component({
  selector: 'app-header',
  providers: [HeaderStore],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [CommonModule, NgOptimizedImage, RouterLink],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('userMenu', { read: ElementRef })
  private readonly userMenu?: ElementRef<HTMLElement>;

  readonly store = inject(HeaderStore);
  readonly user = this.store.user;
  isUserMenuOpen = signal(false);

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly sessionService: SessionService) {}

  ngOnInit(): void {
    merge(of(void 0), this.sessionService.profileUpdated$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.store.load());
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen.update((isOpen) => !isOpen);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  logout(): void {
    this.closeUserMenu();
    this.sessionService.logOut();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (target && this.userMenu?.nativeElement.contains(target)) {
      return;
    }

    this.closeUserMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeUserMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toUserView(rawData: unknown): HeaderUserView {
    return headerUserView(rawData);
  }
}
